import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Skeleton } from '../components/ui/skeleton';
import { CheckCircle2, TrendingUp, Users, Target, Award, Sparkles, ArrowRight, Star, Play, User } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [imageLoading, setImageLoading] = React.useState(true);
  const [imageError, setImageError] = React.useState(false);

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_name: 'Landing Page',
        content_category: 'Funnel'
      });
    }
  }, []);

  const handleCTA = () => {
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout');
    }
    navigate('/video');
  };

  const testimonials = [
    {
      name: "Puujaa Goela",
      role: "Founder - Prayer Group",
      content: "Shiva grew my followers to over 500 and got over 700k views in 1 month. Almost 2000% increase in impressions and 300% increase in engagements.",
      highlight: "It was a great experience working with Shiva from building a strategy to growing my profile from scratch, he tried to deliver whatever was promised from the beginning. His energy and prompt responses made our campaign more effective."
    },
    {
      name: "Thahir",
      role: "Founder - Espanio Ceramiche LLP",
      content: "Shiva helped me with my content and the post-engagement grew by 10x. I highly recommend Shiva for Linkedin Marketing and Branding."
    },
    {
      name: "Dr Nachiket Bhatia",
      role: "CEO - DBMCI",
      content: "Shiva helped me to get to 18K+ followers and 2m+ views in 2 years."
    },
    {
      name: "Tilak Bobby",
      role: "Founder - Resolute",
      content: "Shiva helped me to get to 10K+ followers and 3m+ views in 1 year."
    }
  ];

  const results = [
    { metric: "500+", label: "Followers in 1 Month" },
    { metric: "700K+", label: "Views Generated" },
    { metric: "2000%", label: "Increase in Impressions" },
    { metric: "10X", label: "Engagement Growth" }
  ];

  const benefits = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Build Your Audience",
      description: "$0 reach. Create your own audience without paid ads. A lifetime asset that keeps growing."
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Generate Quality Leads",
      description: "Get 15-30 inbound leads per month from prospects who already know and trust you."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Guaranteed Growth",
      description: "3000 followers in 180 days or we work for free. No questions asked."
    },
    // {
    //   icon: <Award className="w-8 h-8" />,
    //   title: "LinkedIn Top Voice",
    //   description: "Achieve LinkedIn Top Voice status in 3-6 months and become an industry authority."
    // }
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Profile Optimization",
      description: "We revamp your LinkedIn profile to attract your ideal clients and showcase your expertise."
    },
    {
      step: "02",
      title: "Strategic Content",
      description: "16 high-converting posts per month designed to build authority and generate leads."
    },
    {
      step: "03",
      title: "Audience Building",
      description: "Connect with 400+ targeted prospects monthly and engage with key decision-makers."
    },
    {
      step: "04",
      title: "Results & Scale",
      description: "Watch your followers grow, leads pour in, and opportunities multiply exponentially."
    }
  ];

  const faqs = [
    {
      question: "What if I don't get 3000 followers or 15-30 leads?",
      answer: "If we don't deliver 3000 followers and/or 15-30 leads in 180 days, we'll continue working for you completely free until we hit those numbers. No questions asked."
    },
    {
      question: "How much time do I need to invest?",
      answer: "Minimal time from your end. We handle profile optimization, content creation, posting, engagement, and connection building. You just need to approve content weekly and engage with comments occasionally."
    },
    {
      question: "What makes this different from other LinkedIn services?",
      answer: "We're the only service that guarantees specific results or works for free. Plus, our proven process has built 100+ personal brands with measurable results - 700K+ views, 2000% impression increases, and consistent lead generation."
    },
    {
      question: "Do you work with all industries?",
      answer: "We specialize in B2B companies and professionals - founders, CEOs, consultants, and service providers looking to generate leads and build authority on LinkedIn."
    },
    {
      question: "What's included in the service?",
      answer: "Profile optimization, 10 posts per month, daily engagement, 400+ targeted connections monthly, weekly reports, dedicated support via Slack/WhatsApp, and strategy calls."
    },
    {
      question: "How quickly will I see results?",
      answer: "Most clients see increased engagement within the first month. Follower growth accelerates in months 2-3, and lead generation typically starts within 60-90 days as your authority builds."
    }
  ];

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <span className="logo-text">Shiva Dudigama</span>
            </div>
            <nav className="nav">
              <a href="#results" className="nav-link">Results</a>
              <a href="#how-it-works" className="nav-link">How It Works</a>
              <a href="#testimonials" className="nav-link">Testimonials</a>
              <a href="#faq" className="nav-link">FAQ</a>
            </nav>
            <Button onClick={handleCTA} className="cta-button-header">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-glow"></div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <Sparkles className="w-4 h-4" />
              <span>100+ Personal Brands Built</span>
            </div>
            
            <h1 className="hero-title">
              Get <span className="gradient-text">3000 Followers</span> & <span className="gradient-text">15-30 Leads</span> in 180 Days
            </h1>
            
            <p className="hero-subtitle">
              Or We Work For <span className="highlight-text">FREE</span>
            </p>
            
            <p className="hero-description">
              Build a high-impact LinkedIn brand that attracts leads, investors, and top talent — without paid ads. The most permanent asset of your business and life.
            </p>

            <div className="hero-cta-group">
              <Button onClick={handleCTA} size="lg" className="cta-button-primary">
                <Play className="w-5 h-5 mr-2" />
                Watch Free Training
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button onClick={handleCTA} variant="outline" size="lg" className="cta-button-secondary">
                Book Free Consultation
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="trust-indicators">
              <div className="trust-item">
                <div className="stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 star-icon" fill="currentColor" />
                  ))}
                </div>
                <span className="trust-text">4.8G Rating</span>
              </div>
              <div className="trust-divider"></div>
              <div className="trust-item">
                <span className="trust-text">100+ Brands Built</span>
              </div>
              <div className="trust-divider"></div>
              <div className="trust-item">
                <span className="trust-text">50M+ Views Generated</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="founder-section">
        <div className="container">
          <div className="founder-grid">
            <div className="founder-content">
              <h2 className="founder-title">Meet Your Growth Partner</h2>
              <p className="founder-subtitle">Hi, I'm Shiva Dudigama</p>
              <p className="founder-description">
                As a LinkedIn Personal Branding Expert and Growth Strategist, I've helped 100+ founders and CEOs transform their LinkedIn presence from zero to hero. With a proven track record of generating millions of views and thousands of qualified leads, I understand what it takes to build a powerful personal brand that drives business growth.
              </p>
              <div className="founder-stats">
                <div className="stat-item">
                  <span className="stat-number">7+</span>
                  <span className="stat-label">Years Experience</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">100+</span>
                  <span className="stat-label">Brands Built</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">50M+</span>
                  <span className="stat-label">Views Generated</span>
                </div>
              </div>
            </div>
            <div className="founder-image-container">
              <div className="founder-image-wrapper">
                <div className="founder-glow"></div>
                {imageLoading && !imageError && (
                  <div className="founder-image-placeholder">
                    <Skeleton className="w-full h-full absolute inset-0" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <User className="w-16 h-16 text-gray-400" />
                    </div>
                  </div>
                )}
                {imageError ? (
                  <div className="founder-image-error">
                    <User className="w-20 h-20 text-gray-400" />
                    <p className="text-sm text-gray-400 mt-2">Image not available</p>
                  </div>
                ) : (
                  <img 
                    src="MyImage.jpg" 
                    alt="Shiva Dudigama - LinkedIn Growth Expert"
                    className={`founder-image ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                    onLoad={() => setImageLoading(false)}
                    onError={() => {
                      setImageLoading(false);
                      setImageError(true);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section id="results" className="results-section">
        <div className="container">
          <h2 className="section-title">Real Results, Real Impact</h2>
          <div className="results-grid">
            {results.map((result, index) => (
              <Card key={index} className="result-card">
                <div className="result-metric">{result.metric}</div>
                <div className="result-label">{result.label}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="container">
          <div className="problem-content">
            <h2 className="section-title">The Problem With Traditional Marketing</h2>
            <div className="problem-grid">
              <div className="problem-item">
                <div className="problem-icon">❌</div>
                <h3>Paid Ads Are Expensive</h3>
                <p>Burning cash on ads with diminishing returns. High costs, low margins, unsustainable growth.</p>
              </div>
              <div className="problem-item">
                <div className="problem-icon">❌</div>
                <h3>Cold Outreach Doesn't Work</h3>
                <p>B2B buyers stay cold unless they know and trust you. Outbound has lower ROI every year.</p>
              </div>
              <div className="problem-item">
                <div className="problem-icon">❌</div>
                <h3>No Owned Audience</h3>
                <p>You're paying for algorithms and reach you don't own. One change and your pipeline dries up.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section">
        <div className="container">
          <div className="solution-header">
            <h2 className="section-title">The Solution: Build Your Own Audience</h2>
            <p className="section-subtitle">
              LinkedIn personal branding delivers the fastest and biggest ROI for B2B growth. Period.
            </p>
          </div>
          
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <Card key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </Card>
            ))}
          </div>

          <div className="solution-cta">
            <Button onClick={handleCTA} size="lg" className="cta-button-primary">
              Start Building Your Brand
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">Our proven 4-step process to LinkedIn domination</p>
          
          <div className="steps-container">
            {howItWorks.map((step, index) => (
              <div key={index} className="step-item">
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
                {index < howItWorks.length - 1 && <div className="step-connector"></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="testimonials-section">
        <div className="container">
          <h2 className="section-title">What Our Clients Say</h2>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card">
                <div className="testimonial-stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4" fill="currentColor" />
                  ))}
                </div>
                <p className="testimonial-content">"{testimonial.content}"</p>
                {testimonial.highlight && (
                  <p className="testimonial-highlight">"{testimonial.highlight}"</p>
                )}
                <div className="testimonial-author">
                  <div>
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-role">{testimonial.role}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="guarantee-section">
        <div className="container">
          <Card className="guarantee-card">
            <div className="guarantee-content">
              <div className="guarantee-icon">
                <CheckCircle2 className="w-16 h-16" />
              </div>
              <h2 className="guarantee-title">Our Iron-Clad Guarantee</h2>
              <p className="guarantee-text">
                Get <strong>3000 followers and/or 15-30 leads</strong> in the next <strong>180 days</strong> or we work for free until you do.
              </p>
              <p className="guarantee-subtext">
                No questions asked. No fine print. We either deliver or you don't pay.
              </p>
              <Button onClick={handleCTA} size="lg" className="cta-button-primary">
                Claim Your Guarantee Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-container">
            <Accordion type="single" collapsible className="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="faq-question">{faq.question}</AccordionTrigger>
                  <AccordionContent className="faq-answer">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="container">
          <div className="final-cta-content">
            <h2 className="final-cta-title">Ready to Build Your LinkedIn Brand?</h2>
            <p className="final-cta-subtitle">
              Join 100+ founders and CEOs who've transformed their LinkedIn presence into a lead-generating machine.
            </p>
            <Button onClick={handleCTA} size="lg" className="cta-button-primary">
              Get Started Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="final-cta-note">✓ No credit card required • ✓ Free consultation • ✓ 180-day guarantee</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3 className="footer-logo">Shiva Dudigama</h3>
              <p className="footer-tagline">Building Personal Brands Since 2018</p>
            </div>
            <div className="footer-links">
              <a href="#" className="footer-link">Privacy Policy</a>
              <a href="#" className="footer-link">Terms of Service</a>
              <a href="#" className="footer-link">Contact</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2025 Shiva Dudigama. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;